import {
  useContext,
  useCallback,
  createContext,
  useState,
  type ReactNode,
} from "react";

import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import evaluateRestQuery from "@/remoteState/evaluateRestQuery";
import useLazyRestQuery from "@/remoteState/useLazyRestQuery";
import authenticatedServerSideRequestConfig from "@/utils/authenticatedServerSideRequestConfig";
import type { NextPageContext } from "next";

export const fetchUrl = `${MONOLITH_ORIGIN}/api/v3/user/me/experiment/`;

interface IUserExperimentSegmentProviderProps {
  initialValue: IUserExperimentSegmentsQueryContext;
  children: ReactNode;
}

interface IUserExperimentSegmentsQueryContext {
  data?: Record<string, unknown> | undefined;
  loading: boolean;
  error?: Error | undefined;
  refreshData?: () => Promise<void> | undefined;
}

const UserExperimentSegmentsQueryContext =
  createContext<IUserExperimentSegmentsQueryContext>({
    loading: true,
  });

export const UserExperimentSegmentsProvider = ({
  initialValue,
  children,
}: IUserExperimentSegmentProviderProps) => {
  const [getUserExperimentSegments, refetchedUserExperimentSegments] =
    useLazyRestQuery(fetchUrl) as [
      Function,
      IUserExperimentSegmentsQueryContext,
    ];
  const refetchHasResult =
    refetchedUserExperimentSegments.data !== undefined ||
    refetchedUserExperimentSegments.error !== undefined;
  const [userExperimentSegmentQuery, setUserExperimentSegmentQuery] = useState(
    refetchHasResult ? refetchedUserExperimentSegments : initialValue,
  );
  const refreshData = useCallback(async () => {
    const response = await getUserExperimentSegments();
    setUserExperimentSegmentQuery(response);
    return response;
  }, [getUserExperimentSegments]);

  const providerValue = {
    ...userExperimentSegmentQuery,
    refreshData,
  };

  return (
    <UserExperimentSegmentsQueryContext.Provider value={providerValue}>
      {children}
    </UserExperimentSegmentsQueryContext.Provider>
  );
};

UserExperimentSegmentsProvider.__getInitialProps = async ({
  ctx,
}: {
  ctx: NextPageContext;
}) => {
  const request = new Request(
    fetchUrl,
    authenticatedServerSideRequestConfig(ctx),
  );
  const initialValue = await evaluateRestQuery(request);
  return { initialValue };
};

const useUserExperimentSegments = () => {
  return useContext(UserExperimentSegmentsQueryContext);
};

export default useUserExperimentSegments;
